import { Controller, Control } from "react-hook-form";
import { useEffect, useState, useRef } from "react";
import { Autocomplete, Box } from "@mui/material";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import InputTextSearchSelectFetch from "./customTextInput";
import "./SearchSelect.css";

type PropsSearch<ObjectType> = {
  name: string;
  label: string;
  control: Control<any, any>;
  disabled?: boolean;
  inputStyles?: string;
  fetchOptions: (...params: any[]) => Promise<ObjectType[]>;
  propertyLabel: keyof ObjectType;
  propertyLabelDNI?: keyof ObjectType;
  onClear?: () => void;
  required?: boolean;
  onSelected?: (data: ObjectType) => void;
};

export default function SearchSelectFetch<ObjectType extends { id: string }| any>({
  control,
  name,
  label,
  disabled,
  fetchOptions,
  inputStyles,
  propertyLabel,
  propertyLabelDNI,
  onClear,
  required,
  onSelected,
}: PropsSearch<ObjectType>) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ObjectType[]>([]);
  const [loading, setLoading] = useState<boolean>(open);

  const fetchOptionsRef = useRef(fetchOptions);
  fetchOptionsRef.current = fetchOptions;

  useEffect(() => {
    if (open) {
      fetchOptionsRef
        .current()
        .then((optionsFromDb) => {
          setOptions([...optionsFromDb]);
        })
        .catch((e) => {
          setOptions([]);
          console.log(e);
        })
        .finally(() => {
          setLoading(false);
        }); }
  }, [open]);

  return (
    <Controller
      rules={{ required }}
      name={name}
      control={control}
      render={({
        field: { value, onChange, ...restField },
        fieldState: { error },
      }) => (
        <Autocomplete
          disabled={disabled}
          clearOnEscape
          className="SearchAutocomplete"
          popupIcon={<ExpandMoreOutlinedIcon />}
          options={options}
          isOptionEqualToValue={(option, value) =>
            option.id === value.id || option.value === value.value
          }
          getOptionLabel={(option) =>
            option[propertyLabelDNI]
              ? `${option[propertyLabel]} - (${option[propertyLabelDNI]})`
              : option[propertyLabel]
          }
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}>
              {option[propertyLabelDNI]
              ? `${option[propertyLabel]} - (${option[propertyLabelDNI]})`
              : option[propertyLabel]}
            </Box>
          )}
          loading={loading}
          value={value || null}
          renderInput={(params) => (
            <InputTextSearchSelectFetch
              params={params}
              isOpen={open}
              label={label}
              error={error}
              loading={loading}
              styles={inputStyles}
              disabled={disabled}
            />
          )}
          onChange={(e, data: ObjectType, reason) => {
            if (onClear && reason === "clear") onClear();
            if (onSelected && reason === "selectOption") onSelected(data);
            return onChange(data);
          }}
          open={open}
          onOpen={() => {
            setOpen(true);
            setLoading(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          {...restField}
        />
      )}
    />
  );
}
